import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  RefreshControl,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useAppDispatch, useAppSelector } from '../store';
import {
  fetchPremiumAnalytics,
  refreshAnalytics,
  setActiveTab,
  selectPremiumAnalytics,
  selectAnalyticsDashboard,
  selectAnalyticsLoading,
  selectAnalyticsError,
  selectActiveTab,
  selectDataFreshness,
  selectConfidence,
} from '../store/slices/premiumAnalyticsSlice';
import { useAuth } from '@coffeeplexity/shared';
import { Ionicons } from '@expo/vector-icons';

// Import premium analytics components
import { PremiumAnalyticsOverview } from '../components/ai/premium/PremiumAnalyticsOverview';
import { PerformanceTrendsView } from '../components/ai/premium/PerformanceTrendsView';
import { AdvancedSkillAssessmentView } from '../components/ai/premium/AdvancedSkillAssessmentView';
import { EfficiencyOptimizationView } from '../components/ai/premium/EfficiencyOptimizationView';
import { PremiumAnalyticsHeader } from '../components/ai/premium/PremiumAnalyticsHeader';
import { DataFreshnessIndicator } from '../components/ai/premium/DataFreshnessIndicator';
import { EnhancedCoachingView } from '../components/ai/premium/EnhancedCoachingView';
import { theme } from '../theme';

// const { width } = Dimensions.get('window');

type TabType = 'overview' | 'trends' | 'coaching' | 'skills' | 'efficiency';

interface TabConfig {
  id: TabType;
  title: string;
  icon: string;
  description: string;
}

const tabs: TabConfig[] = [
  {
    id: 'overview',
    title: 'Overview',
    icon: 'analytics-outline',
    description: 'Learning analytics and AI insights',
  },

  {
    id: 'trends',
    title: 'Trends',
    icon: 'trending-up-outline',
    description: 'Performance trends and analysis',
  },

  {
    id: 'coaching',
    title: 'Coaching',
    icon: 'school-outline',
    description: 'Enhanced recommendations',
  },
  {
    id: 'skills',
    title: 'Skills',
    icon: 'library-outline',
    description: 'Advanced skill assessment',
  },
  {
    id: 'efficiency',
    title: 'Efficiency',
    icon: 'speedometer-outline',
    description: 'Learning optimization',
  },
];

export const PremiumAnalyticsDashboardScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const premiumAnalytics = useAppSelector(selectPremiumAnalytics);
  const dashboard = useAppSelector(selectAnalyticsDashboard);
  const isLoading = useAppSelector(selectAnalyticsLoading);
  const error = useAppSelector(selectAnalyticsError);
  const activeTab = useAppSelector(selectActiveTab);
  const dataFreshness = useAppSelector(selectDataFreshness);
  const confidence = useAppSelector(selectConfidence);

  useEffect(() => {
    if (user?.id && !dashboard) {
      dispatch(
        fetchPremiumAnalytics({
          userId: user.id,
          includeComparisons: true,
          timeframe: 'month',
        })
      );
    }
  }, [dispatch, user?.id, dashboard]);

  const handleRefresh = async () => {
    if (!user?.id) return;

    setIsRefreshing(true);
    try {
      await dispatch(refreshAnalytics(user.id)).unwrap();
    } catch (error) {
      Alert.alert(
        'Refresh Failed',
        'Unable to refresh analytics data. Please try again.',
        [{ text: 'OK' }]
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleTabPress = (tabId: TabType) => {
    dispatch(setActiveTab(tabId));
  };

  const renderTabBar = () => (
    <View style={styles.tabBar}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabScrollContent}
      >
        {tabs.map(tab => (
          <TouchableOpacity
            key={tab.id}
            style={[styles.tab, activeTab === tab.id && styles.activeTab]}
            onPress={() => handleTabPress(tab.id)}
          >
            <Ionicons
              name={tab.icon as any}
              size={20}
              color={activeTab === tab.id ? '#6750A4' : '#666'}
            />
            <Text
              style={[
                styles.tabText,
                activeTab === tab.id && styles.activeTabText,
              ]}
            >
              {tab.title}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderContent = () => {
    if (!dashboard) {
      return (
        <View style={styles.emptyState}>
          <Ionicons name='analytics-outline' size={64} color='#ccc' />
          <Text style={styles.emptyStateText}>No analytics data available</Text>
          <Text style={styles.emptyStateSubtext}>
            Complete some learning activities to see your analytics
          </Text>
        </View>
      );
    }

    switch (activeTab) {
      case 'overview':
        return <PremiumAnalyticsOverview dashboard={dashboard} />;
      case 'trends':
        return <PerformanceTrendsView trends={dashboard.performanceTrends} />;
      case 'coaching':
        return (
          <EnhancedCoachingView
            recommendations={dashboard.coachingRecommendations}
            analytics={dashboard.learningAnalytics}
          />
        );
      case 'skills':
        return (
          <AdvancedSkillAssessmentView assessment={dashboard.skillAssessment} />
        );
      case 'efficiency':
        return (
          <EfficiencyOptimizationView
            optimization={dashboard.efficiencyOptimization}
          />
        );
      default:
        return <PremiumAnalyticsOverview dashboard={dashboard} />;
    }
  };

  if (error) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.errorContainer}>
          <Ionicons name='warning-outline' size={64} color='#FF6B6B' />
          <Text style={styles.errorTitle}>Analytics Unavailable</Text>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={() =>
              user?.id &&
              dispatch(
                fetchPremiumAnalytics({
                  userId: user.id,
                  forceRefresh: true,
                })
              )
            }
          >
            <Text style={styles.retryButtonText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <PremiumAnalyticsHeader
        title='Premium Analytics'
        subscriptionTier={dashboard?.subscriptionTier || 'premium'}
        onUpgrade={() => {
          // Handle upgrade navigation
          Alert.alert('Upgrade', 'Navigate to subscription upgrade');
        }}
      />

      <DataFreshnessIndicator
        dataFreshness={dataFreshness}
        confidence={confidence}
        lastUpdated={premiumAnalytics.lastUpdated}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />

      {renderTabBar()}

      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            colors={['#6750A4']}
            tintColor='#6750A4'
          />
        }
        showsVerticalScrollIndicator={false}
      >
        {isLoading && !dashboard ? (
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading analytics...</Text>
          </View>
        ) : (
          renderContent()
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  tabBar: {
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: 8,
  },
  tabScrollContent: {
    paddingHorizontal: 16,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
  },
  activeTab: {
    backgroundColor: '#EDE7F6',
  },
  tabText: {
    marginLeft: 6,
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
  },
  activeTabText: {
    color: '#6750A4',
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
    marginTop: 16,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 32,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginTop: 16,
    textAlign: 'center',
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
    textAlign: 'center',
    lineHeight: 20,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
    marginTop: 16,
    textAlign: 'center',
  },
  errorText: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
    textAlign: 'center',
    lineHeight: 20,
  },
  retryButton: {
    backgroundColor: '#6750A4',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 24,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default PremiumAnalyticsDashboardScreen;
